/**
 * Phase 4: lokale Kopie, gepufferte Änderungen und Abgleich.
 */
import type { QueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { idbClear, idbGet, idbKeys } from './idb';
import { flushOutbox, onFlushed, refreshCounts } from './outbox';
import { getOfflineState, onReconnect, setOfflineState } from './state';
import { FILE_CACHE, pullCampaign, setSyncQueryClient } from './sync';

export { useOfflineState } from './state';

/** Zuletzt geöffnete Kampagne: nach einer Unterbrechung wird sie zuerst abgeglichen. */
let activeCampaign: string | null = null;
const PERIODIC_MS = 5 * 60_000;

export async function initOffline(qc: QueryClient): Promise<void> {
  setSyncQueryClient(qc);
  await refreshCounts();
  const lastSync: Record<string, string> = {};
  for (const key of await idbKeys('meta')) {
    const v = key.startsWith('lastSync:') ? await idbGet<string>('meta', key) : undefined;
    if (v) lastSync[key.slice('lastSync:'.length)] = v;
  }
  setOfflineState({ lastSync: { ...getOfflineState().lastSync, ...lastSync } });

  onReconnect(() => {
    void flushOutbox().then(() => {
      if (activeCampaign) void pullCampaign(activeCampaign);
    });
    // Geöffnete Ansichten mit dem Server-Stand auffrischen
    void qc.invalidateQueries();
  });
  // Nach dem Senden den Server-Stand übernehmen (enthält die eigenen Änderungen, ggf. Folgeänderungen)
  onFlushed((cids) => {
    for (const cid of cids) void pullCampaign(cid);
  });
  if (getOfflineState().pending > 0) void flushOutbox();
}

/** Kampagne offline verfügbar halten: beim Öffnen und regelmäßig abgleichen. */
export function useOfflineSync(cid: string): void {
  useEffect(() => {
    if (!cid) return;
    activeCampaign = cid;
    void pullCampaign(cid);
    const timer = window.setInterval(() => void pullCampaign(cid), PERIODIC_MS);
    return () => window.clearInterval(timer);
  }, [cid]);
}

/** Nach Echtzeit-Ereignissen (auch eigenen Änderungen) gebündelt abgleichen. */
let pullTimer: number | undefined;
export function schedulePull(cid: string, delay = 800): void {
  window.clearTimeout(pullTimer);
  pullTimer = window.setTimeout(() => void pullCampaign(cid), delay);
}

/** Beim Abmelden: lokale Kopie, Puffer und Bilder vom Gerät entfernen. */
export async function clearOfflineData(): Promise<void> {
  for (const s of ['responses', 'outbox', 'meta', 'problems'] as const) await idbClear(s);
  if (typeof caches !== 'undefined') {
    try {
      await caches.delete(FILE_CACHE);
    } catch {
      /* egal */
    }
  }
  setOfflineState({ pending: 0, problems: 0, lastSync: {} });
}

export { flushOutbox, pullCampaign };
